import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/atomic/Card";
import { Dialog, DialogContent, DialogTitle } from "@/components/atomic/Dialog";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { routes } from "@/configs/router.config/routes";
import { AppContext } from "@/contexts/AppContext";
import useConfig from "@/hooks/useConfig";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";

interface Props {
  className?: string;
  displayGroups: { name: string; id: string; }[];
  isOpenDisplayGroupSelect: boolean;
  setIsOpenDisplayGroupSelect: (value: boolean) => void;
}

const DisplayGroupSelectCard: React.FC<Props> = (
  { className = "", displayGroups = [], isOpenDisplayGroupSelect, setIsOpenDisplayGroupSelect },
) => {
  const { t } = useTranslation("voiceek_ui");
  const { config } = useConfig();
  const { setIsComparisionMode } = useContext(AppContext);
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  // 部署選択時の処理
  const handleDisplayGroupSelect = () => {
    setIsOpenDisplayGroupSelect(false);
    setIsComparisionMode(false); // 比較モードを解除
  };

  return (
    <Dialog open={isOpenDisplayGroupSelect} onOpenChange={setIsOpenDisplayGroupSelect}>
      <DialogContent
        className={cn(
          "bg-transparent flex flex-row justify-center w-[260px] p-0 border-0 rounded-0 shadow-[2px_2px_4px_#00000040]",
          "data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100",
          "data-[state=closed]:slide-out-to-right-[0] data-[state=closed]:slide-out-to-top-[0] data-[state=open]:slide-in-from-right-[0] data-[state=open]:slide-in-from-top-[0]",
          className,
        )}
      >
        <VisuallyHidden>
          <DialogTitle />
        </VisuallyHidden>
        <div className="relative w-full overflow-hidden">
          <ScrollArea viewportClassName="max-h-96" className="w-full">
            <div className="flex flex-col w-full h-full items-start gap-1 px-2 py-3 bg-white">
              <Link to={"/"} className="w-full" onClick={handleDisplayGroupSelect}>
                <Card className="w-full bg-white border-none shadow-none hover:bg-[#6888E1] transition-colors">
                  <CardContent className="flex items-center h-10 px-5 py-2.5 rounded-[10px] hover:text-white transition-colors">
                    {t("departments.all")}
                  </CardContent>
                </Card>
              </Link>

              <h2 className="w-fit text-[#5F6368] font-bold px-2 text-base">
                {t("departments.select", { displayGroupLabel: config?.displayGroupLabel })}
              </h2>

              {displayGroups.map((dg) => (
                <Link
                  key={dg.id}
                  to={routes.analysis(sessionCurrentWorkspaceId)(dg.id)}
                  className="w-full"
                  onClick={handleDisplayGroupSelect}
                >
                  <Card
                    key={dg.id}
                    className="w-full bg-white border-none shadow-none hover:bg-[#6888E1] transition-colors"
                  >
                    <CardContent className="block items-center h-10 pl-5 pr-0 py-2.5 rounded-[10px] hover:text-white truncate max-w-[230px] transition-colors">
                      {dg.name}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DisplayGroupSelectCard;
